/* R148 · Feb 2026 — Work Locations settings (HR-admin only).
 * CRUD for locations (name, address, geo lat/lng, radius, WiFi SSID allowlist)
 * + per-location member assignments. Enforced by the backend clock-in
 * validator when hr_settings.enforce_location_checkin=true. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { MapPin, Plus, Trash2, Wifi, Users, Edit3, MapPinned } from "lucide-react";
import MultiSelect from "@/components/ui/multi-select";
import { apiErrorMessage } from "@/lib/apiError";

const EMPTY = { name: "", address: "", lat: "", lng: "", radius_m: 200, allowed_wifi_ssids: [], is_active: true };

export default function WorkLocationsSettings({ wsId }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [editing, setEditing] = useState(null); // location row or EMPTY
  const [assignFor, setAssignFor] = useState(null);
  const [members, setMembers] = useState([]);

  const load = async () => {
    if (!wsId) return;
    setBusy(true);
    try {
      const [{ data: locs }, { data: ms }] = await Promise.all([
        api.get(`/workspaces/${wsId}/work-locations`),
        api.get(`/workspaces/${wsId}/members`).catch(() => ({ data: [] })),
      ]);
      setRows(locs || []);
      setMembers(ms || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load locations"));
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId]);

  const remove = async (loc) => {
    if (!window.confirm(`Delete "${loc.name}"? All member assignments will be removed.`)) return;
    try {
      await api.delete(`/work-locations/${loc.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <div className="space-y-3" data-testid="work-locations-settings">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="label-eyebrow">Work locations</div>
          <p className="text-xs text-muted-foreground mt-1">
            Define geo-fences (company office, client sites, co-working) and optional WiFi SSID allowlists.
            When <b>Enforce location check-in</b> is ON in HR Settings, members can only clock in from an assigned location.
          </p>
        </div>
        <Button size="sm" className="rounded-none" onClick={() => setEditing({ ...EMPTY })} data-testid="wl-add-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add location
        </Button>
      </div>

      {busy && rows.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-border p-8 text-center text-sm text-muted-foreground" data-testid="wl-empty">
          No work locations yet. Add your primary office as a starting point.
        </div>
      ) : (
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-muted/40 border-b border-border">
                <th className="text-left px-3 py-2 label-eyebrow text-[9px]">Name</th>
                <th className="text-left px-3 py-2 label-eyebrow text-[9px]">Address</th>
                <th className="text-left px-3 py-2 label-eyebrow text-[9px]">Geo</th>
                <th className="text-left px-3 py-2 label-eyebrow text-[9px]">Radius</th>
                <th className="text-left px-3 py-2 label-eyebrow text-[9px]">WiFi SSIDs</th>
                <th className="text-left px-3 py-2 label-eyebrow text-[9px]">Active</th>
                <th className="text-right px-3 py-2 label-eyebrow text-[9px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l.id} className="border-b border-border hover:bg-muted/20" data-testid={`wl-row-${l.id}`}>
                  <td className="px-3 py-2 font-medium">{l.name}</td>
                  <td className="px-3 py-2 max-w-[240px] truncate" title={l.address}>{l.address || "—"}</td>
                  <td className="px-3 py-2 font-mono text-[10px]">
                    {l.lat != null && l.lng != null
                      ? <a href={`https://www.google.com/maps?q=${l.lat},${l.lng}`} target="_blank" rel="noreferrer" className="underline">{Number(l.lat).toFixed(4)}, {Number(l.lng).toFixed(4)}</a>
                      : "—"}
                  </td>
                  <td className="px-3 py-2 font-mono">{l.radius_m}m</td>
                  <td className="px-3 py-2">
                    {(l.allowed_wifi_ssids || []).length === 0 ? "—" : (
                      <div className="flex flex-wrap gap-1">
                        {l.allowed_wifi_ssids.map((s) => (
                          <span key={s} className="inline-flex items-center gap-0.5 border border-border bg-muted/50 px-1.5 py-0.5 text-[10px]"><Wifi className="w-2.5 h-2.5" />{s}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">{l.is_active ? "Yes" : "No"}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-1">
                      <Button size="sm" variant="ghost" className="rounded-none h-6 px-1.5 text-xs" onClick={() => setAssignFor(l)} data-testid={`wl-assign-${l.id}`}>
                        <Users className="w-3 h-3 mr-1" />Assign
                      </Button>
                      <Button size="sm" variant="ghost" className="rounded-none h-6 px-1.5" onClick={() => setEditing({ ...l })} data-testid={`wl-edit-${l.id}`}>
                        <Edit3 className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="rounded-none h-6 px-1.5 text-red-600" onClick={() => remove(l)} data-testid={`wl-delete-${l.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <LocationEditor wsId={wsId} loc={editing} onClose={() => setEditing(null)} onSaved={load} />}
      {assignFor && <AssignmentsDialog wsId={wsId} loc={assignFor} members={members} onClose={() => setAssignFor(null)} />}
    </div>
  );
}

function LocationEditor({ wsId, loc, onClose, onSaved }) {
  const isEdit = !!loc.id;
  const [form, setForm] = useState({
    name: loc.name || "",
    address: loc.address || "",
    lat: loc.lat ?? "",
    lng: loc.lng ?? "",
    radius_m: loc.radius_m || 200,
    allowed_wifi_ssids: (loc.allowed_wifi_ssids || []).join(", "),
    is_active: loc.is_active !== false,
  });
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation unavailable"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6) }));
        setLocating(false);
        toast.success("Location pinned to your current position");
      },
      (err) => { setLocating(false); toast.error(err.code === 1 ? "Location permission denied" : "Couldn't fetch location"); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        address: form.address || null,
        lat: form.lat === "" ? null : parseFloat(form.lat),
        lng: form.lng === "" ? null : parseFloat(form.lng),
        radius_m: parseInt(form.radius_m, 10) || 200,
        allowed_wifi_ssids: form.allowed_wifi_ssids.split(",").map((s) => s.trim()).filter(Boolean),
        is_active: !!form.is_active,
      };
      if (isEdit) await api.patch(`/work-locations/${loc.id}`, body);
      else await api.post(`/workspaces/${wsId}/work-locations`, body);
      toast.success(isEdit ? "Location updated" : "Location added");
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="wl-editor">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit location" : "Add work location"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="label-eyebrow mb-1">Name <span className="text-amber-500">*</span></div>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Head Office / Bandra WeWork / Client site" data-testid="wl-name" />
          </div>
          <div>
            <div className="label-eyebrow mb-1">Address</div>
            <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Full postal address (optional, for reference)" className="min-h-[60px]" data-testid="wl-address" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <div className="label-eyebrow mb-1">Latitude</div>
              <Input value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} placeholder="19.0760" data-testid="wl-lat" />
            </div>
            <div>
              <div className="label-eyebrow mb-1">Longitude</div>
              <Input value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} placeholder="72.8777" data-testid="wl-lng" />
            </div>
            <div>
              <div className="label-eyebrow mb-1">Radius (m)</div>
              <Input type="number" min="25" max="5000" value={form.radius_m} onChange={(e) => setForm({ ...form, radius_m: e.target.value })} data-testid="wl-radius" />
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={useMyLocation} disabled={locating} className="rounded-none" data-testid="wl-use-my-location">
            <MapPinned className="w-3.5 h-3.5 mr-1.5" />{locating ? "Locating…" : "Pin to my current location"}
          </Button>
          <div>
            <div className="label-eyebrow mb-1">Allowed WiFi SSIDs</div>
            <Input value={form.allowed_wifi_ssids} onChange={(e) => setForm({ ...form, allowed_wifi_ssids: e.target.value })} placeholder="Office_5G, Office_Guest (comma-separated)" data-testid="wl-ssids" />
            <p className="text-[10px] text-muted-foreground mt-1">Users on any of these WiFi networks bypass the geofence check.</p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="wl-active" />
            Active
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose} data-testid="wl-cancel">Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={saving} data-testid="wl-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignmentsDialog({ wsId, loc, members, onClose }) {
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/workspaces/${wsId}/work-locations/${loc.id}/assignments`)
      .then((r) => setSelected((r.data || []).map((a) => a.user_id)))
      .catch(() => setSelected([]))
      .finally(() => setBusy(false));
  }, [wsId, loc.id]);

  const opts = members.filter((m) => m.status === "active").map((m) => ({
    value: m.user_id,
    label: m.user?.name || m.user?.email || m.user_id,
  })).sort((a, b) => a.label.localeCompare(b.label));

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/workspaces/${wsId}/work-locations/${loc.id}/assignments`, { location_id: loc.id, user_ids: selected });
      toast.success(`Assigned ${selected.length} member(s)`);
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="wl-assignments">
        <DialogHeader>
          <DialogTitle>Assign members · {loc.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Members you assign here can clock in from this location. Someone can be assigned to multiple locations.
          </p>
          {busy ? (
            <div className="text-sm text-muted-foreground py-4 text-center">Loading…</div>
          ) : (
            <MultiSelect
              options={opts}
              value={selected}
              onChange={setSelected}
              placeholder="Search & pick teammates…"
              allLabel="Assign members"
              maxChips={4}
              width="w-full"
              testid="wl-assign-members"
            />
          )}
          <div className="text-xs text-muted-foreground"><MapPin className="inline w-3 h-3 mr-1" />{selected.length} member(s) selected</div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose} data-testid="wl-assign-cancel">Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={saving} data-testid="wl-assign-save">{saving ? "Saving…" : "Save assignments"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
